<template>
  <div class="px-4 py-6">
    <div class="relative flex flex-col bg-white w-full mb-6 shadow-lg rounded-lg">
      <div class="purchase-order-header">
        <div>
          <h2 class="text-2xl font-semibold text-blueGray-700">
            Purchase Request
          </h2>
          <p class="purchase-order-subtitle">
            Quick report view for purchase requests.
          </p>
        </div>

        <button
          @click="openModal()"
          class="bg-lightBlue-600 text-white text-base font-bold uppercase px-5 py-3 rounded shadow hover:bg-lightBlue-700 transition-all purchase-create-btn"
        >
          Create Purchase Request
        </button>
      </div>

      <!-- Desktop / tablet -->
      <div class="hidden md:block w-full overflow-x-auto">
        <table class="w-full border-collapse purchase-order-table">
          <thead>
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-blueGray-500 uppercase">
                Purchase Date
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-blueGray-500 uppercase">
                Item Description
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-blueGray-500 uppercase">
                Price
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-blueGray-500 uppercase">
                Status
              </th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-blueGray-500 uppercase">
                Advanced View
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="order in sortedPurchaseOrders"
              :key="order.id"
              class="purchase-order-row hover:bg-blueGray-50"
            >
              <td class="px-6 py-4 text-sm font-semibold text-blueGray-700 whitespace-nowrap">
                {{ formatDateTime(order.created_at) }}
              </td>

              <td class="px-6 py-4 text-sm font-semibold text-blueGray-700">
                {{ shortenText(order.description || "No description", 42) }}
              </td>

              <td class="px-6 py-4 text-sm font-semibold text-blueGray-700 whitespace-nowrap">
                {{ formatCurrency(order.price) }}
              </td>

              <td class="px-6 py-4 text-sm">
                <span
                  class="px-3 py-1 text-xs font-semibold rounded-full"
                  :class="statusBadgeClass(order.status)"
                >
                  {{ displayStatus(order.status) }}
                </span>
              </td>

              <td class="px-6 py-4 text-sm">
                <button
                  class="bg-emerald-600 text-white text-xs font-bold uppercase px-4 py-2 rounded shadow hover:bg-emerald-700 transition-all"
                  @click="openAdvancedView(order)"
                >
                  Advanced View
                </button>
              </td>
            </tr>

            <tr v-if="!sortedPurchaseOrders.length">
              <td colspan="5" class="text-center py-6 text-blueGray-400">
                No purchase requests yet.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile -->
      <div class="md:hidden purchase-order-mobile-list">
        <div
          v-for="order in sortedPurchaseOrders"
          :key="`mobile-${order.id}`"
          class="purchase-order-mobile-card"
        >
          <div class="mobile-order-top">
            <div class="mobile-order-date">
              {{ formatDateTime(order.created_at) }}
            </div>

            <span
              class="px-3 py-1 text-xs font-semibold rounded-full mobile-status-badge"
              :class="statusBadgeClass(order.status)"
            >
              {{ displayStatus(order.status) }}
            </span>
          </div>

          <div class="mobile-order-row">
            <div class="mobile-order-label">Item Description</div>
            <div class="mobile-order-value">
              {{ shortenText(order.description || "No description", 64) }}
            </div>
          </div>

          <div class="mobile-order-row">
            <div class="mobile-order-label">Price</div>
            <div class="mobile-order-value">
              {{ formatCurrency(order.price) }}
            </div>
          </div>

          <div class="mobile-order-actions">
            <button
              class="bg-emerald-600 text-white text-sm font-bold uppercase px-4 py-3 rounded shadow hover:bg-emerald-700 transition-all"
              @click="openAdvancedView(order)"
            >
              Advanced View
            </button>
          </div>
        </div>

        <div v-if="!sortedPurchaseOrders.length" class="purchase-order-mobile-empty">
          No purchase requests yet.
        </div>
      </div>
    </div>

    <PurchaseOrderModal
      :isOpen="showModal"
      :editMode="editMode"
      :orderData="selectedOrder"
      @close="closeModal"
      @save="saveOrder"
    />
  </div>
</template>

<script>
import PurchaseOrderModal from "@/components/Modal/CreatePurchasOrderModal.vue";
import Swal from "sweetalert2";

export default {
  name: "PurchaseOrders",
  components: { PurchaseOrderModal },
  data() {
    return {
      showModal: false,
      editMode: false,
      selectedOrder: null,
      purchaseOrders: [],
    };
  },
  computed: {
    sortedPurchaseOrders() {
      return [...this.purchaseOrders].sort((a, b) => {
        const aDate = new Date(a.created_at || 0);
        const bDate = new Date(b.created_at || 0);
        return bDate - aDate;
      });
    },
  },
  methods: {
    openModal(order = null) {
      this.editMode = !!order;
      this.selectedOrder = order ? { ...order } : null;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    formatCurrency(value) {
      if (!value && value !== 0) return "";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },

    formatDateTime(value) {
      if (!value) return "No date";
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) return "No date";

      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },

    shortenText(text, maxLength = 40) {
      if (!text) return "";
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    },

    displayStatus(status) {
      if (!status) return "Processing";

      const normalized = String(status).toLowerCase();

      if (normalized === "approved") return "Approved";
      if (normalized === "rejected") return "Denied";
      if (normalized === "denied") return "Denied";
      if (normalized === "pending") return "Processing";

      return status;
    },

    statusBadgeClass(status) {
      const normalized = String(status || "").toLowerCase();

      if (normalized === "approved") {
        return "bg-green-100 text-emerald-700";
      }

      if (normalized === "rejected" || normalized === "denied") {
        return "bg-red-100 text-red-600";
      }

      return "bg-yellow-100 text-amber-700";
    },

    extractStoreName(link) {
      if (!link) return "No store";

      try {
        const url = new URL(link);
        let host = url.hostname.replace(/^www\./, "");

        if (host.includes("amazon")) return "Amazon";
        if (host.includes("walmart")) return "Walmart";
        if (host.includes("target")) return "Target";
        if (host.includes("costco")) return "Costco";
        if (host.includes("ebay")) return "eBay";
        if (host.includes("bestbuy")) return "Best Buy";

        return host;
      } catch (error) {
        return "External link";
      }
    },

    openAdvancedView(order) {
      const canEdit =
        order.status === "Pending" || order.status === "Rejected";

      const itemLinkHtml = order.link
        ? `<a href="${order.link}" target="_blank" rel="noopener noreferrer" style="color:#2563eb; text-decoration:underline; word-break:break-all;">${order.link}</a>`
        : `<span style="color:#94a3b8;">No link</span>`;

      const shippingHtml = order.shipping_address
        ? `<div class="advanced-view-row"><div class="advanced-view-label">Shipping Address</div><div class="advanced-view-value">${order.shipping_address}</div></div>`
        : "";

      Swal.fire({
        title: "Purchase Request Details",
        width: 760,
        showConfirmButton: false,
        showCloseButton: true,
        html: `
          <div style="text-align:left;">
            <div class="advanced-view-grid">
              <div class="advanced-view-row">
                <div class="advanced-view-label">Purchase Date</div>
                <div class="advanced-view-value">${this.formatDateTime(order.created_at)}</div>
              </div>

              <div class="advanced-view-row">
                <div class="advanced-view-label">Status</div>
                <div class="advanced-view-value">${this.displayStatus(order.status)}</div>
              </div>

              <div class="advanced-view-row">
                <div class="advanced-view-label">Price</div>
                <div class="advanced-view-value">${this.formatCurrency(order.price)}</div>
              </div>

              <div class="advanced-view-row">
                <div class="advanced-view-label">Store</div>
                <div class="advanced-view-value">${this.extractStoreName(order.link)}</div>
              </div>

              <div class="advanced-view-row">
                <div class="advanced-view-label">Item Description</div>
                <div class="advanced-view-value">${order.description || "No description"}</div>
              </div>

              <div class="advanced-view-row">
                <div class="advanced-view-label">Item Link</div>
                <div class="advanced-view-value">${itemLinkHtml}</div>
              </div>

              ${shippingHtml}
            </div>

            <div style="display:flex; gap:10px; flex-wrap:wrap; margin-top:18px;">
              ${
                order.link
                  ? `<a href="${order.link}" target="_blank" rel="noopener noreferrer" class="swal2-confirm swal2-styled" style="background:#059669; text-decoration:none;">Open Link</a>`
                  : ""
              }

              ${
                canEdit
                  ? `<button id="po-edit-btn" class="swal2-confirm swal2-styled" style="background:#0284c7;">Edit</button>`
                  : ""
              }

              ${
                canEdit
                  ? `<button id="po-delete-btn" class="swal2-cancel swal2-styled" style="background:#dc2626;">Delete</button>`
                  : ""
              }
            </div>
          </div>
        `,
        didOpen: () => {
          const editBtn = document.getElementById("po-edit-btn");
          const deleteBtn = document.getElementById("po-delete-btn");

          if (editBtn) {
            editBtn.addEventListener("click", () => {
              Swal.close();
              this.openModal(order);
            });
          }

          if (deleteBtn) {
            deleteBtn.addEventListener("click", () => {
              Swal.close();
              this.deleteOrder(order.id);
            });
          }
        },
      });
    },

    async saveOrder(order) {
      const token = localStorage.getItem("token");
      const url = this.editMode
        ? `${process.env.VUE_APP_BACKEND_DOMAIN}/api/purchase-orders/${order.id}`
        : `${process.env.VUE_APP_BACKEND_DOMAIN}/api/purchase-orders`;

      const method = this.editMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(order),
      });

      const result = await response.json();

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: this.editMode
            ? "Purchase Request Updated!"
            : "Purchase Request Created!",
          text: result.message,
          showConfirmButton: false,
          timer: 2000,
        });
        this.closeModal();
        this.fetchOrders();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error Saving Request",
          text: result.errors
            ? Object.values(result.errors).flat().join("\n")
            : result.error || "Something went wrong.",
        });
      }
    },

    async deleteOrder(id) {
      const confirmDelete = await Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (confirmDelete.isConfirmed) {
        const token = localStorage.getItem("token");
        const url = `${process.env.VUE_APP_BACKEND_DOMAIN}/api/purchase-orders/${id}`;

        const response = await fetch(url, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const result = await response.json();

        if (response.ok) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: result.message || "Purchase request deleted successfully.",
            showConfirmButton: false,
            timer: 2000,
          });
          this.fetchOrders();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error Deleting",
            text: result.error || "Failed to delete the request.",
          });
        }
      }
    },

    async fetchOrders() {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.VUE_APP_BACKEND_DOMAIN}/api/purchase-orders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      this.purchaseOrders = Array.isArray(data) ? data : [];
    },
  },
  mounted() {
    this.fetchOrders();
  },
};
</script>

<style scoped>
.purchase-order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin: 0.75rem 0.75rem 1.25rem;
  flex-wrap: wrap;
}

.purchase-order-subtitle {
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.95rem;
}

.purchase-create-btn {
  white-space: nowrap;
}

.purchase-order-table th,
.purchase-order-table td {
  border-bottom: 1px solid #e2e8f0;
}

.purchase-order-row:last-child td {
  border-bottom: 0;
}

.purchase-order-mobile-list {
  padding: 0 0.75rem 1rem;
}

.purchase-order-mobile-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.mobile-order-top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.85rem;
}

.mobile-order-date {
  font-size: 0.82rem;
  font-weight: 700;
  color: #64748b;
}

.mobile-status-badge {
  white-space: nowrap;
}

.mobile-order-label {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.mobile-order-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

.mobile-order-row {
  margin-bottom: 0.85rem;
}

.mobile-order-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
  margin-top: 1rem;
}

.purchase-order-mobile-empty {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 1rem;
  padding: 1rem;
  color: #64748b;
  text-align: center;
}

@media (max-width: 767px) {
  .purchase-order-header {
    margin: 0.75rem 0.75rem 1rem;
  }

  .purchase-create-btn {
    width: 100%;
    text-align: center;
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
}
</style>
